import { cva } from "class-variance-authority";
//#region src/components/shared/form-field-variants.ts
/**
* Shared layout for labeled single-value fields (Select, DateInput, etc.).
*/
const fieldWrapperVariants = cva("gap-xxs flex min-w-0 flex-col font-body", {
	defaultVariants: { size: "base" },
	variants: { size: {
		base: "",
		large: ""
	} }
});
/**
* Default label treatment for form fields inside `fieldWrapperVariants`.
*
* Matches {@link TextInput}’s `TextInputLabel` typography: base size uses
* `font-heading-medium`, large uses `font-body-regular` (no root weight).
*/
const labelVariants = cva(["tracking-sm m-0"], {
	defaultVariants: {
		disabled: false,
		size: "base"
	},
	variants: {
		disabled: {
			false: "text-text-primary",
			true: "text-text-disabled"
		},
		size: {
			base: "text-body-small leading-body-small font-heading-medium",
			large: "text-body-small leading-body-small font-body-regular"
		}
	}
});
/**
* Helper and error copy below the field control.
*/
const helperTextVariants = cva("tracking-sm m-0", {
	compoundVariants: [
		{
			className: "text-text-danger",
			disabled: false,
			error: true
		},
		{
			className: "text-text-disabled",
			disabled: true,
			error: true
		},
		{
			className: "text-text-secondary",
			disabled: false,
			error: false
		}
	],
	defaultVariants: { disabled: false },
	variants: {
		disabled: {
			false: "",
			true: "text-text-disabled"
		},
		error: {
			false: "",
			true: ""
		},
		size: {
			base: "text-body-xsmall leading-body-xsmall font-heading-medium",
			large: "text-body-small leading-body-small font-body-regular"
		}
	}
});
/**
* Small-viewport typography override for input-like controls.
*
* iOS Safari zooms focused text inputs when their font size drops below 16px,
* so mobile viewports use Tailwind's `text-base` and revert to the design
* token typography from `sm` upward.
*/
const mobileSafeInputTextSizeClasses = {
	base: "text-base leading-6 sm:text-body-base sm:leading-body-base",
	large: "text-base leading-6 sm:text-body-medium sm:leading-body-medium"
};
const valueTextVariants = cva("min-w-0 flex-auto bg-transparent pr-[16px] text-inherit shadow-none outline-none ring-0", {
	defaultVariants: { size: "base" },
	variants: { size: {
		base: `${mobileSafeInputTextSizeClasses.base} font-heading-medium`,
		large: `${mobileSafeInputTextSizeClasses.large} font-heading-medium`
	} }
});
const iconVariants = cva("", { variants: { size: {
	base: "h-[9px] w-[9px]",
	large: "h-[12px] w-[12px]"
} } });
/**
* Select trigger — uses `data-[disabled]` from Base UI Select.Item/Trigger.
* Heights match Button (`base` 32px / `large` 36px) for adjacent control alignment.
*/
const selectTriggerVariants = cva("relative m-0 flex w-full appearance-none items-center gap-xs box-border rounded-xs border border-(length:--border-width-default) border-border-input bg-background-input px-xs py-0 text-left text-text-primary shadow-xs outline-none data-[disabled]:bg-background-disabled data-[disabled]:border-border-disabled data-[disabled]:text-text-disabled data-[disabled]:cursor-not-allowed", {
	defaultVariants: {
		error: false,
		size: "base"
	},
	variants: {
		error: {
			false: "not-data-[disabled]:not-focus:hover:bg-background-input-hover not-data-[disabled]:active:bg-background-input-pressed not-data-[disabled]:active:border-border-focused not-data-[disabled]:focus:bg-background-input-pressed not-data-[disabled]:focus:border-border-focused",
			true: "border-border-danger not-data-[disabled]:not-focus:hover:bg-background-input-hover"
		},
		size: {
			base: `h-[32px] ${mobileSafeInputTextSizeClasses.base} font-heading-medium`,
			large: `h-[36px] ${mobileSafeInputTextSizeClasses.large} font-heading-medium`
		}
	}
});
/**
* Native button trigger (DateInput) — same visual spec as `selectTriggerVariants`
* but uses `:disabled` / `enabled:` instead of `data-[disabled]`.
*/
const dateInputTriggerVariants = cva("relative m-0 flex w-full min-w-0 cursor-pointer appearance-none items-center gap-xs box-border rounded-xs border border-(length:--border-width-default) border-border-input bg-background-input px-xs py-0 text-left text-text-primary shadow-xs outline-none transition-colors disabled:cursor-not-allowed disabled:bg-background-disabled disabled:border-border-disabled disabled:text-text-disabled", {
	defaultVariants: {
		error: false,
		size: "base"
	},
	variants: {
		error: {
			false: "enabled:not-focus:hover:bg-background-input-hover enabled:active:bg-background-input-pressed enabled:active:border-border-focused enabled:focus:bg-background-input-pressed enabled:focus:border-border-focused enabled:focus:outline-none",
			true: "border-border-danger enabled:not-focus:hover:bg-background-input-hover"
		},
		size: {
			base: `h-[32px] ${mobileSafeInputTextSizeClasses.base} font-heading-medium`,
			large: `h-[36px] ${mobileSafeInputTextSizeClasses.large} font-heading-medium`
		}
	}
});
//#endregion
export { dateInputTriggerVariants, fieldWrapperVariants, helperTextVariants, iconVariants, labelVariants, mobileSafeInputTextSizeClasses, selectTriggerVariants, valueTextVariants };

//# sourceMappingURL=form-field-variants.js.map